import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import type { unitMapping } from '@sorare/core/src/lib/style';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BuyPackButton } from 'components/commonCard/BuyPackButton';

import { Ellipse } from './Ellipse';
import type { PackWithBuyButton_cardPack } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({
  center: true,
})`
  position: relative;
  isolation: isolate;
  will-change: transform;
  transition: transform 0.3s ease-in-out;
  &.scaleOnHover:hover,
  &.scaleOnHover:focus {
    transform: scale(1.02);
  }
`;

const Postionned = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  > svg {
    width: 100%;
    height: 100%;
  }
`;
const PackImg = styled(GlareEffect)`
  object-fit: contain;
  height: 100%;
  width: calc(12 * var(--unit));
  @media ${mobileAndAbove} {
    width: calc(16 * var(--unit));
  }
  .isPremiumPack & {
    width: calc(18 * var(--unit));
    @media ${mobileAndAbove} {
      width: calc(20 * var(--unit));
    }
  }
`;

type Props = {
  cardPack: PackWithBuyButton_cardPack;
  cardPackGroupId: string;
  onClickBuy: (cardPackGroupId: string, slug: string) => void;
  gap?: keyof typeof unitMapping;
  isPremiumPack?: boolean;
  scaleOnHover?: boolean;
};
export const PackWithBuyButton = ({
  cardPack,
  cardPackGroupId,
  onClickBuy,
  gap = 1,
  isPremiumPack,
  scaleOnHover = true,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  return (
    <Root
      center
      gap={gap}
      className={classNames({
        scaleOnHover,
        isPremiumPack,
      })}
    >
      {!!isPremiumPack && (
        <Postionned>
          <Ellipse color={cardPack.color} />
        </Postionned>
      )}
      <Link
        to={`${generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, {
          params: {
            group: idFromObject(cardPackGroupId),
          },
        })}?pack=${cardPack.slug}`}
      >
        <PackImg
          pictureUrl={proxyUrl(cardPack.pictureUrl, {
            cropWidth: 160,
          })}
        />
      </Link>

      <BuyPackButton
        cardPack={cardPack}
        onClick={() => onClickBuy(cardPackGroupId, cardPack.slug)}
        size="small"
        color={isPremiumPack ? 'primary' : 'secondary'}
      />
    </Root>
  );
};

PackWithBuyButton.fragments = {
  cardPack: gql`
    fragment PackWithBuyButton_cardPack on CardPack {
      slug
      pictureUrl
      color
      ...BuyPackButton_cardPack
    }
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<PackWithBuyButton_cardPack>,
};
